import { Play } from "@phosphor-icons/react";
import { useState } from "react";
import { armStressResetDemo } from "./demo-arm";
import { useCornerReveal } from "./use-corner-reveal";

/**
 * Arms the pre-login demo from the sign-in page.
 *
 * Same corner and same reveal as the demo panel's own button, because an
 * operator who has learned one should not have to learn the other. It sits on
 * the only screen where the panel cannot: there is no member yet, so the
 * panel's permission read has nothing to ask about.
 *
 * DEV builds only. The panel falls back to a server-checked flag when the
 * build is not DEV, and that check needs an identity this page does not have.
 * Rather than invent a weaker gate for a deployed build, the button simply is
 * not there, and the demo runs from a dev machine.
 */
export function PreLoginDemoButton() {
  /*
   * The corner governs visibility whether or not the demo is armed. Pinning it
   * open after a press left a control sitting over the sign-in page for the
   * rest of the session, which is both untidy and a worse confirmation than
   * the label change: the cursor is still in the corner at the moment of the
   * click, so the operator reads "Armed" and then it goes away with everything
   * else when they move off.
   */
  const [revealed] = useCornerReveal(true);
  const [focused, setFocused] = useState(false);
  const [armed, setArmed] = useState(false);

  /*
   * Focus is held beside the corner rather than pushed into it. Calling the
   * hook's setter on focus looked equivalent and was not: a pointermove out of
   * the corner leaves an 800ms fade pending, and that timer fires regardless,
   * setting `revealed` false while the button still holds focus. The control
   * then goes invisible and unclickable under a keyboard user who is standing
   * on it, and Enter arms the demo with nothing on screen.
   */
  const visible = revealed || focused;

  if (!import.meta.env.DEV) return null;
  if (import.meta.env.VITE_HIDE_DEMO_PANEL === "true") return null;
  if (import.meta.env.VITE_E2E_MODE === "true") return null;

  return (
    <button
      type="button"
      onClick={() => {
        armStressResetDemo();
        setArmed(true);
      }}
      aria-label={
        armed
          ? "Demo armed. It runs 20 seconds after day one finishes."
          : "Arm the stress reset demo"
      }
      title={armed ? "Armed. Sign in to run it." : "Arm the stress reset demo"}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={`fixed right-5 bottom-5 z-50 flex h-8 items-center gap-2 rounded-md bg-navy/15 px-2.5 text-ink-primary/55 backdrop-blur-md backdrop-saturate-150 transition-opacity duration-300 ease-out hover:bg-navy/35 hover:text-ink-primary/90 focus-visible:bg-navy/35 focus-visible:text-ink-primary/90 ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <Play size={14} weight="fill" aria-hidden="true" />
      <span className="font-medium text-xs">{armed ? "Armed" : "Demo"}</span>
    </button>
  );
}
